import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/context/ThemeContextProvider';
import { useBaseStore } from '@/store';
import { appConfigActionsSelector } from '@/store/appConfigSlice';
import { useQueryClient } from '@tanstack/react-query';
import { Key, Laptop, LogOut, Moon, Sparkles, Sun } from 'lucide-react';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { getHeaders } from '@/api/utils';

interface ProfileMenuProps {
  trigger: ReactNode;
  className?: string;
}

const ProfileMenu = ({ trigger, className }: ProfileMenuProps) => {
  const { onChangePasswordDialogOpen } = useBaseStore(appConfigActionsSelector);

  const navigate = useNavigate();
  const { setTheme } = useTheme();

  const { logout, user, profile } = useBaseStore((state) => ({
    logout: state.authSlice.actions.logout,
    user: state.authSlice.state.user,
    profile: state.authSlice.state.profile,
  }));

  const queryClient = useQueryClient();

  const profileLabelMap: Record<string, string> = {
    ADMINISTRADOR: 'ADM',
    ADMINISTRADOR_REVENDA: 'ADM Revenda',
    NOVO: 'Novo',
    REPRESENTANTE: 'Representante',
  };

  const formattedProfile =
    profileLabelMap[profile || ''] || profile || 'Usuário';
  const appVersion = getHeaders()['x-versao'] || 'v1.0.0';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={className}>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" sideOffset={8}>
        <div className="px-2 py-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-sm font-semibold text-foreground truncate">
              {user || 'Usuário'}
            </span>
            <Badge variant="subtle" className="text-[10px] px-1.5 py-0">
              {formattedProfile}
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground font-normal">
            Versão:{' '}
            <span className="font-medium text-foreground">{appVersion}</span>
          </div>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuLabel className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
            Minha Conta
          </DropdownMenuLabel>
          <DropdownMenuItem
            className="gap-2 text-sm cursor-pointer"
            onClick={onChangePasswordDialogOpen}
          >
            <Key className="size-4 text-muted-foreground" />
            Alterar senha
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center justify-between text-xs text-muted-foreground font-medium uppercase tracking-wider">
            <span>Tema</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-normal text-amber-600 dark:text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
              <Sparkles className="size-3" />
              Beta
            </span>
          </DropdownMenuLabel>
          <DropdownMenuItem
            className="gap-2 text-sm cursor-pointer"
            onClick={() => setTheme('light')}
          >
            <Sun className="size-4 text-muted-foreground" />
            Claro
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2 text-sm cursor-pointer"
            onClick={() => setTheme('dark')}
          >
            <Moon className="size-4 text-muted-foreground" />
            Escuro
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2 text-sm cursor-pointer"
            onClick={() => setTheme('system')}
          >
            <Laptop className="size-4 text-muted-foreground" />
            Sistema
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="gap-2 text-sm text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer"
          onClick={() => {
            logout(queryClient);
            navigate('/login');
          }}
        >
          <LogOut className="size-4" />
          Sair da conta
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileMenu;
